import { Controller } from "@hotwired/stimulus"

// Refine Studio edit-region painter. The operator brushes over the part of
// the finished render the refinement applies to; on submit we export a PNG
// alpha mask at the generation's native resolution (opaque = preserved,
// painted strokes = transparent / editable) into the hidden refine_mask file
// input. gpt-image's edits endpoint uses it as a hard lock.
//
// Strokes are stored in normalized (0..1) coordinates so the small display
// canvas and the full-resolution export stay in sync.
export default class extends Controller {
  static targets = ["zone", "image", "canvas", "input", "brush", "toggleLabel", "status"]
  static values = { width: Number, height: Number }

  connect() {
    this.paths = []
    this.drawing = false
    this.prepared = false
  }

  toggle(event) {
    event.preventDefault()
    this.zoneTarget.hidden = !this.zoneTarget.hidden
    if (!this.zoneTarget.hidden) this.#sizeCanvas()
  }

  // ---- drawing -------------------------------------------------------------

  startStroke(event) {
    event.preventDefault()
    this.#sizeCanvas()
    this.drawing = true
    this.paths.push({ r: this.#brushRadius(), points: [this.#normPoint(event)] })
    this.#redraw()
  }

  moveStroke(event) {
    if (!this.drawing) return
    event.preventDefault()
    this.paths[this.paths.length - 1].points.push(this.#normPoint(event))
    this.#redraw()
  }

  endStroke() {
    this.drawing = false
    this.#updateStatus()
  }

  clear(event) {
    event.preventDefault()
    this.paths = []
    this.#redraw()
    this.#updateStatus()
  }

  // ---- submit --------------------------------------------------------------

  beforeSubmit(event) {
    if (this.prepared || this.paths.length === 0) return // nothing painted → plain submit

    event.preventDefault()
    // The RENDERED image is the truth, not the record's `size`. Those disagree
    // routinely: Seedream is asked for an upscaled multiple of the requested
    // ratio (1600x2000 is sent as 3280x4096), so the record keeps what was
    // ordered while the file is something else. Exporting the mask at the
    // recorded size produced "mask size does not match image size" from the
    // OpenAI edits endpoint, which requires them equal to the pixel.
    const W = this.imageTarget.naturalWidth || this.widthValue
    const H = this.imageTarget.naturalHeight || this.heightValue
    if (!W || !H) return // can't build a mask — submit without one

    const out = document.createElement("canvas")
    out.width = W
    out.height = H
    const ctx = out.getContext("2d")
    ctx.fillStyle = "#000000"
    ctx.fillRect(0, 0, W, H)                       // fully opaque = preserved
    ctx.globalCompositeOperation = "destination-out" // strokes punch transparency
    this.#strokePaths(ctx, W, H, "rgba(0,0,0,1)")

    out.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], "refine_mask.png", { type: "image/png" })
        const dt = new DataTransfer()
        dt.items.add(file)
        this.inputTarget.files = dt.files
      }
      this.prepared = true
      this.element.requestSubmit ? this.element.requestSubmit() : this.element.submit()
    }, "image/png")
  }

  // ---- internals -----------------------------------------------------------

  #sizeCanvas() {
    const img = this.imageTarget
    const c = this.canvasTarget
    const w = img.clientWidth
    const h = img.clientHeight
    if (!w || !h) {
      img.addEventListener("load", () => this.#sizeCanvas(), { once: true })
      return
    }
    if (c.width !== w || c.height !== h) {
      c.width = w
      c.height = h
      this.#redraw()
    }
  }

  #brushRadius() {
    const v = this.hasBrushTarget ? parseInt(this.brushTarget.value, 10) : 5
    return (v || 5) / 100 // normalized radius, fraction of image width
  }

  #normPoint(event) {
    const rect = this.canvasTarget.getBoundingClientRect()
    return [
      Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height))
    ]
  }

  #strokePaths(ctx, w, h, style) {
    ctx.strokeStyle = style
    ctx.fillStyle = style
    ctx.lineCap = "round"
    ctx.lineJoin = "round"
    this.paths.forEach((path) => {
      const lw = Math.max(1, path.r * w * 2)
      if (path.points.length === 1) {
        const [nx, ny] = path.points[0]
        ctx.beginPath()
        ctx.arc(nx * w, ny * h, lw / 2, 0, Math.PI * 2)
        ctx.fill()
        return
      }
      ctx.lineWidth = lw
      ctx.beginPath()
      path.points.forEach(([nx, ny], i) => {
        const x = nx * w
        const y = ny * h
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
      })
      ctx.stroke()
    })
  }

  #redraw() {
    const c = this.canvasTarget
    const ctx = c.getContext("2d")
    ctx.clearRect(0, 0, c.width, c.height)
    this.#strokePaths(ctx, c.width, c.height, "rgba(220, 53, 69, 0.45)")
  }

  #updateStatus() {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent =
      this.paths.length > 0 ? `${this.paths.length} stroke${this.paths.length > 1 ? "s" : ""} — only the painted area will change.` : ""
  }
}
